import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material';
import { AppService } from 'src/@core/services/app.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { CreateTaskDialogComponent } from '../../entity-leads/create-task-dialog/create-task-dialog.component';
import {
  TaskActionDialogComponent,
  TaskActionDialogMode,
  TaskActionDialogResult,
} from '../../entity-leads/task-action-dialog/task-action-dialog.component';
import { TaskListItem, TasksService } from '../../entity-leads/services/tasks.service';
import {
  bucketForEffective,
  EffectiveTaskStatus,
  formatTaskDateTime,
  getEffectiveTaskStatus,
  parseTaskDate,
  TaskBucket,
} from '../../entity-leads/utils/task-status.util';

type BucketFilter = 'all' | TaskBucket;
type WhenFilter = 'all' | 'past' | 'today' | 'upcoming';

interface TaskRowVm {
  task: TaskListItem;
  status: EffectiveTaskStatus;
  bucket: TaskBucket;
  scheduledMs: number | null;
  searchText: string;
}

/** Page size when pulling a project's tasks; the list is paged locally afterwards. */
const FETCH_PAGE_SIZE = 100;
/** Safety stop so a runaway project can't loop forever (10 000 tasks). */
const MAX_FETCH_PAGES = 100;

/**
 * Tasks tab inside a project, laid out like the Connected tab: header with actions,
 * status cards that double as filters, then one table. Every task carries a
 * date-aware status (see task-status.util.ts), so a task dated in the past shows as
 * Executed, Failed, Cancelled or Not executed, never as a stale "Scheduled".
 */
@Component({
  selector: 'hour4u-admin-project-tasks',
  templateUrl: './project-tasks.component.html',
  styleUrls: ['./project-tasks.component.scss']
})
export class ProjectTasksComponent implements OnChanges, OnDestroy {
  @Input() projectDetails: any;

  profile = this.appService.getCurrentAccount();

  rows: TaskRowVm[] = [];
  isLoading = false;
  loadError = '';
  /** True when we stopped paging at the safety limit, so counts are a floor. */
  truncated = false;
  loadedAt: Date | null = null;

  bucketFilter: BucketFilter = 'all';
  whenFilter: WhenFilter = 'all';
  ownerFilter: '' | 'ai' | 'human' = '';
  searchQuery = '';

  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [25, 50, 100];

  isActionLoading = false;
  expandedId: string | null = null;

  /** Bumped per load so a slow, older response can't overwrite a newer one. */
  private loadSeq = 0;

  readonly cards: Array<{ key: BucketFilter; label: string; tone: string; hint: string }> = [
    { key: 'all', label: 'All tasks', tone: 'neutral', hint: 'Everything on this project' },
    { key: 'executed', label: 'Executed', tone: 'success', hint: 'Ran successfully' },
    { key: 'not_executed', label: 'Not executed', tone: 'danger', hint: 'Time passed, never ran' },
    { key: 'failed', label: 'Failed', tone: 'danger', hint: 'Ran but did not succeed' },
    { key: 'upcoming', label: 'Upcoming', tone: 'info', hint: 'Scheduled for later' },
    { key: 'awaiting_approval', label: 'Needs approval', tone: 'warning', hint: 'Waiting on you' },
    { key: 'running', label: 'Running', tone: 'live', hint: 'In progress now' },
    { key: 'paused', label: 'Paused', tone: 'warning', hint: 'On hold' },
    { key: 'cancelled', label: 'Cancelled', tone: 'neutral', hint: 'Rejected or cancelled' },
  ];

  constructor(
    private appService: AppService,
    private tasksService: TasksService,
    private dialog: MatDialog,
    private notify: NotificationService,
    private cd: ChangeDetectorRef,
  ) { }

  get entityId(): string {
    return this.profile && this.profile.entity && this.profile.entity.id;
  }

  get projectId(): string {
    return this.projectDetails && this.projectDetails.project_id;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.projectDetails && this.projectId) {
      const prev = changes.projectDetails.previousValue;
      if (!prev || prev.project_id !== this.projectId) {
        this.load();
      }
    }
  }

  ngOnDestroy() {
    this.loadSeq++;
  }

  /** Fetches every page for this project, then derives statuses against "now". */
  load() {
    if (!this.entityId || !this.projectId) {
      return;
    }
    const seq = ++this.loadSeq;
    this.isLoading = true;
    this.loadError = '';
    this.truncated = false;
    const collected: TaskListItem[] = [];

    const fetchPage = (page: number) => {
      this.tasksService.getTasks({
        entity_id: this.entityId,
        project_id: this.projectId,
        pageNum: page,
        pageSize: FETCH_PAGE_SIZE,
      }).subscribe(
        (res) => {
          if (seq !== this.loadSeq) {
            return;
          }
          const data = (res && res.data) || [];
          data.forEach((t) => collected.push(t));
          const more = !!(res && res.pagination && res.pagination.has_next_page) && data.length > 0;
          if (more && page < MAX_FETCH_PAGES) {
            fetchPage(page + 1);
            return;
          }
          this.truncated = more;
          this.finishLoad(collected);
        },
        (err) => {
          if (seq !== this.loadSeq) {
            return;
          }
          this.isLoading = false;
          this.rows = [];
          this.loadError = (err && err.error && (err.error.error || err.error.message)) || 'Could not load tasks.';
          this.cd.markForCheck();
        }
      );
    };
    fetchPage(1);
  }

  private finishLoad(tasks: TaskListItem[]) {
    const now = new Date();
    const seen = new Set<string>();
    this.rows = tasks
      .filter((t) => {
        // Defensive: a page boundary shift can repeat a row; never count one twice.
        if (!t || !t.task_id || seen.has(t.task_id)) {
          return false;
        }
        seen.add(t.task_id);
        return true;
      })
      .map((task) => {
        const status = getEffectiveTaskStatus(task as any, now);
        const when = parseTaskDate(task.scheduled_at);
        return {
          task,
          status,
          bucket: bucketForEffective(status),
          scheduledMs: when ? when.getTime() : null,
          searchText: [task.lead_name, task.lead_number, task.title, task.outcome, task.owner_name, task.channel]
            .filter(Boolean).join(' ').toLowerCase(),
        } as TaskRowVm;
      })
      // Newest first, undated last: the most relevant tasks are at the top.
      .sort((a, b) => (b.scheduledMs || 0) - (a.scheduledMs || 0));
    this.loadedAt = now;
    this.isLoading = false;
    this.pageIndex = 0;
    this.cd.markForCheck();
  }

  // ---- counts & filtering -------------------------------------------------

  countFor(key: BucketFilter): number {
    if (key === 'all') {
      return this.rows.length;
    }
    return this.rows.filter((r) => r.bucket === key).length;
  }

  get filteredRows(): TaskRowVm[] {
    const q = this.searchQuery.trim().toLowerCase();
    const now = new Date();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const dayEnd = dayStart + 86400000;
    return this.rows.filter((r) => {
      if (this.bucketFilter !== 'all' && r.bucket !== this.bucketFilter) {
        return false;
      }
      if (this.ownerFilter && (r.task.owner_type || '').toLowerCase() !== this.ownerFilter) {
        return false;
      }
      if (q && r.searchText.indexOf(q) === -1) {
        return false;
      }
      if (this.whenFilter !== 'all') {
        const ms = r.scheduledMs;
        if (ms === null) {
          return false;
        }
        if (this.whenFilter === 'past' && ms >= dayStart) { return false; }
        if (this.whenFilter === 'today' && (ms < dayStart || ms >= dayEnd)) { return false; }
        if (this.whenFilter === 'upcoming' && ms < dayEnd) { return false; }
      }
      return true;
    });
  }

  get pagedRows(): TaskRowVm[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredRows.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
  }

  get showingLabel(): string {
    const total = this.filteredRows.length;
    if (!total) {
      return 'Showing 0';
    }
    const start = this.pageIndex * this.pageSize + 1;
    const end = Math.min(total, (this.pageIndex + 1) * this.pageSize);
    return `Showing ${start}–${end} of ${total}`;
  }

  get hasActiveFilters(): boolean {
    return this.bucketFilter !== 'all' || this.whenFilter !== 'all' || !!this.ownerFilter || !!this.searchQuery.trim();
  }

  /** Headline sentence for the header: what actually happened, in one line. */
  get headline(): string {
    const total = this.rows.length;
    if (!total) {
      return 'No tasks on this project yet';
    }
    return `${total} ${total === 1 ? 'task' : 'tasks'} on this project`;
  }

  setBucket(key: BucketFilter) {
    this.bucketFilter = this.bucketFilter === key ? 'all' : key;
    this.pageIndex = 0;
  }

  setWhen(w: WhenFilter) {
    this.whenFilter = w;
    this.pageIndex = 0;
  }

  setOwner(o: '' | 'ai' | 'human') {
    this.ownerFilter = o;
    this.pageIndex = 0;
  }

  onSearch(value: string) {
    this.searchQuery = value;
    this.pageIndex = 0;
  }

  clearFilters() {
    this.bucketFilter = 'all';
    this.whenFilter = 'all';
    this.ownerFilter = '';
    this.searchQuery = '';
    this.pageIndex = 0;
  }

  onPageSize(size: string) {
    this.pageSize = Number(size);
    this.pageIndex = 0;
  }

  go(delta: number) {
    this.pageIndex = Math.max(0, Math.min(this.totalPages - 1, this.pageIndex + delta));
  }

  toggleExpand(row: TaskRowVm) {
    this.expandedId = this.expandedId === row.task.task_id ? null : row.task.task_id;
  }

  trackByTask(_: number, row: TaskRowVm) {
    return row.task.task_id;
  }

  // ---- display helpers ----------------------------------------------------

  when(value: string): string {
    return formatTaskDateTime(value);
  }

  channelLabel(channel: string): string {
    return channel ? channel.toUpperCase() : '—';
  }

  typeLabel(type: string): string {
    return type ? type.replace(/_/g, ' ') : '—';
  }

  // ---- actions ------------------------------------------------------------

  openCreateTask(mode: 'ai' | 'human') {
    if (!this.projectDetails) {
      return;
    }
    const ref = this.dialog.open(CreateTaskDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: ['add-edit-staff-dialog', 'create-task-dialog-panel'],
      disableClose: true,
      data: {
        mode,
        entityId: this.entityId,
        projects: [this.projectDetails],
        defaultProjectId: this.projectId,
        defaultOutcome: null,
        outcomeOptions: [],
      },
    });
    ref.afterClosed().subscribe((saved) => saved && this.load());
  }

  openEditTask(row: TaskRowVm, event?: Event) {
    if (event) { event.stopPropagation(); }
    const ref = this.dialog.open(CreateTaskDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: ['add-edit-staff-dialog', 'create-task-dialog-panel'],
      disableClose: true,
      data: {
        mode: 'edit',
        entityId: this.entityId,
        projects: [this.projectDetails],
        editTask: row.task,
      },
    });
    ref.afterClosed().subscribe((saved) => saved && this.load());
  }

  canApprove(row: TaskRowVm): boolean {
    const t = row.task;
    return row.status.key === 'awaiting_approval' || row.status.key === 'approval_overdue'
      ? true
      : !!(t.actions && t.actions.can_approve && !row.status.isFinal);
  }

  canReject(row: TaskRowVm): boolean {
    return this.canApprove(row) || !!(row.task.actions && row.task.actions.can_reject && !row.status.isFinal);
  }

  canPause(row: TaskRowVm): boolean {
    return !row.status.isFinal && !!(row.task.actions && row.task.actions.can_pause);
  }

  canEdit(row: TaskRowVm): boolean {
    return !row.status.isFinal && !!(row.task.actions && row.task.actions.can_edit);
  }

  approve(row: TaskRowVm, event?: Event) {
    this.runAction(row, 'approve', (r) =>
      this.tasksService.approveTask(row.task.task_id, { when: r.when, scheduled_at: r.scheduled_at }),
      'Task approved.', event);
  }

  reject(row: TaskRowVm, event?: Event) {
    this.runAction(row, 'reject', (r) =>
      this.tasksService.rejectTask(row.task.task_id, r.reason), 'Task rejected.', event);
  }

  pause(row: TaskRowVm, event?: Event) {
    this.runAction(row, 'pause', (r) =>
      this.tasksService.pauseTask(row.task.task_id, r.reason), 'Task paused.', event);
  }

  private runAction(
    row: TaskRowVm,
    mode: TaskActionDialogMode,
    call: (r: TaskActionDialogResult) => any,
    successMsg: string,
    event?: Event,
  ) {
    if (event) { event.stopPropagation(); }
    const ref = this.dialog.open(TaskActionDialogComponent, {
      width: '520px',
      maxHeight: '90vh',
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      data: {
        mode,
        taskTitle: row.task.title,
        leadName: row.task.lead_name,
        scheduledAt: row.task.scheduled_at,
      },
    });
    ref.afterClosed().subscribe((result: TaskActionDialogResult) => {
      if (!result || !result.confirmed) {
        return;
      }
      this.isActionLoading = true;
      call(result).subscribe(
        () => {
          this.isActionLoading = false;
          this.notify.showMessage(successMsg, 2000);
          this.load();
        },
        (err: any) => {
          this.isActionLoading = false;
          const msg = (err && err.error && (err.error.error || err.error.message)) || 'Action failed.';
          this.notify.showMessage(msg, 3000);
        }
      );
    });
  }
}
